import { useState } from 'react'
import { DataSourceCard } from './DataSourceCard'
import type { DataSource } from '@/lib/api/types'
import { Database, Loader2 } from 'lucide-react'

interface DataSourceListProps {
  dataSources: DataSource[]
  loading: boolean
  onToggle: (id: string, enabled: boolean) => Promise<void>
  onTest: (id: string) => Promise<{ success: boolean; message: string }>
  onEdit: (dataSource: DataSource) => void
  onDelete: (dataSource: DataSource) => void
  onRefresh: (id: string) => Promise<void>
}

export function DataSourceList({
  dataSources,
  loading,
  onToggle,
  onTest,
  onEdit,
  onDelete,
  onRefresh,
}: DataSourceListProps) {
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set())
  const [testingIds, setTestingIds] = useState<Set<string>>(new Set())
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set())
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({})

  const systemSources = dataSources.filter((ds) => ds.isSystemSource)
  const customSources = dataSources.filter((ds) => !ds.isSystemSource)

  const handleToggle = async (id: string, enabled: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(id))
    try {
      await onToggle(id, enabled)
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const handleTest = async (id: string) => {
    setTestingIds((prev) => new Set(prev).add(id))
    try {
      const result = await onTest(id)
      setTestResults((prev) => ({ ...prev, [id]: result }))
    } finally {
      setTestingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const handleRefresh = async (id: string) => {
    setRefreshingIds((prev) => new Set(prev).add(id))
    try {
      await onRefresh(id)
    } finally {
      setRefreshingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        <p className="text-neutral-500 dark:text-neutral-400">Loading data sources...</p>
      </div>
    )
  }

  if (dataSources.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Database className="h-12 w-12 text-neutral-400" />
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          No data sources configured
        </h3>
        <p className="text-neutral-500 dark:text-neutral-400 text-center max-w-md">
          Data sources allow you to connect dynamic fields in your templates to external data
          like user profiles, CRM systems, or custom APIs.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {systemSources.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            System Sources
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
            Built-in data sources that are automatically available for all templates.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {systemSources.map((dataSource) => (
              <DataSourceCard
                key={dataSource.id}
                dataSource={dataSource}
                onToggle={handleToggle}
                onTest={handleTest}
                onEdit={onEdit}
                onDelete={onDelete}
                onRefresh={handleRefresh}
                testResult={testResults[dataSource.id]}
                isToggling={togglingIds.has(dataSource.id)}
                isTesting={testingIds.has(dataSource.id)}
                isRefreshing={refreshingIds.has(dataSource.id)}
              />
            ))}
          </div>
        </div>
      )}

      {customSources.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Custom Sources
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
            Custom data sources you have configured for your organization.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {customSources.map((dataSource) => (
              <DataSourceCard
                key={dataSource.id}
                dataSource={dataSource}
                onToggle={handleToggle}
                onTest={handleTest}
                onEdit={onEdit}
                onDelete={onDelete}
                onRefresh={handleRefresh}
                testResult={testResults[dataSource.id]}
                isToggling={togglingIds.has(dataSource.id)}
                isTesting={testingIds.has(dataSource.id)}
                isRefreshing={refreshingIds.has(dataSource.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
